import type { GetState, SetState } from 'zustand';
import type { ProductPageProps } from '@product/ProductPage/ProductPage';
import type { ProductPageStore } from './useProductPageStore';
import type { DeliveryTime } from '@libs/constants';
import type { ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import type { UnitCode } from '@icommerce/units';

export interface VariantAxis {
  attribute: string;
  label: string;
  items: VariantAxisItem[];
  readonly: boolean;
}

export type VariantAxisItem =
  | {
      unit: UnitCode;
      amount: number;
      calculations?: Record<string, number>;
    }
  | string
  | number;

export interface SkuDeliveryTime {
  sku: string;
  skuDisplayName: string;
  deliveryTime: DeliveryTime;
}

export interface VariantSelectionSlice {
  availableVariantAxes?: VariantAxis[];
  selectedVariantSku?: string;
  selectedVariantAxesValues?: Map<string, string>;
  lastModifiedVariantAxisValue?: { key: string; value: string };
  setAvailableVariantAxes: (axes: VariantAxis[]) => void;
  setSelectedVariantSku: (sku: string) => void;
  setVariantAxisValue: (value: string | ProductAttributeValueAmountUnit, key: string) => void;
  reset: () => void;
}

export const createVariantSelectionSlice = (
  set: SetState<ProductPageStore>,
  get: GetState<ProductPageStore>,
  initialData: ProductPageProps
) =>
  ({
    availableVariantAxes: initialData.availableVariantAxes,
    selectedVariantSku: initialData.selectedVariant,
    selectedVariantAxesValues: initialData.selectedVariantAxesValues,
    lastModifiedVariantAxisValue: undefined,
    setAvailableVariantAxes: axes => {
      set(state => ({
        ...state,
        availableVariantAxes: axes,
      }));
    },
    setSelectedVariantSku: sku => {
      set(state => ({
        ...state,
        selectedVariantSku: sku,
      }));
    },
    setVariantAxisValue: (value: string, key: string) => {
      set(state => {
        const newSelectedVariantAxesValues = new Map(state.selectedVariantAxesValues);
        newSelectedVariantAxesValues.set(key, value);
        return {
          ...state,
          selectedVariantAxesValues: newSelectedVariantAxesValues,
          lastModifiedVariantAxisValue: { key, value },
        };
      });
    },
    reset: () => {
      set(state => ({
        ...state,
        availableVariantAxes: initialData.availableVariantAxes,
        selectedVariant: initialData.selectedVariant,
        selectedVariantAxesValues: initialData.selectedVariantAxesValues,
      }));
    },
  } as VariantSelectionSlice);
